import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import { startRedsysCheckout } from '../utils/redsysCheckout';
import './CSS/LandingPage.css';

function LandingPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const { refreshUserData } = useAuth(false);

  const [landingPage, setLandingPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [hasAccess, setHasAccess] = useState(null);
  const [isCheckingAccess, setIsCheckingAccess] = useState(false);
  const [accessMessage, setAccessMessage] = useState('');

  useEffect(() => {
    const fetchLandingPage = async () => {
      setLoading(true);
      setNotFound(false);
      setLandingPage(null);
      try {
        const response = await fetch(`${backendUrl}/landing-page/public/${slug}`);
        const result = await response.json();
        if (result.success) {
          setLandingPage(result.landingPage);
          document.title = result.landingPage.metaTitle || result.landingPage.product.title;
          const metaDesc = document.querySelector('meta[name="description"]');
          if (metaDesc) metaDesc.setAttribute('content', result.landingPage.metaDescription || '');
        } else {
          setNotFound(true);
        }
      } catch (error) {
        console.error('Error fetching landing page:', error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchLandingPage();
  }, [slug, backendUrl]);

  useEffect(() => {
    if (!landingPage || !landingPage.product) return;

    // Public products: skip permission check, grant access directly
    if (landingPage.product.isPublic === true) {
      setHasAccess(true);
      return;
    }

    const authToken = localStorage.getItem('auth-token');
    if (!authToken) { setHasAccess(null); return; }

    const checkAccess = async () => {
      setIsCheckingAccess(true);
      try {
        const response = await fetch(
          `${backendUrl}/permissions/check/${landingPage.product._id}`,
          { headers: { 'auth-token': authToken } }
        );
        if (response.ok) {
          const data = await response.json();
          if (data.hasPermission) setHasAccess(true);
          else if (data.purchasePending) { setHasAccess(false); setAccessMessage('Your purchase is pending approval.'); }
          else setHasAccess(false);
        } else { setHasAccess(false); }
      } catch (error) { setHasAccess(null); }
      finally { setIsCheckingAccess(false); }
    };
    checkAccess();
  }, [landingPage, backendUrl]);

  const handleCTA = () => {
    // Public products: go directly to player, no login needed
    if (landingPage.product.isPublic === true) {
      navigate(`/product/${landingPage.product._id}`);
      return;
    }
    const authToken = localStorage.getItem('auth-token');
    if (!authToken) { navigate('/account'); return; }
    navigate(`/product/${landingPage.product._id}`);
  };

  const handleBuyNow = async () => {
    const product = landingPage.product;

    // Card payment enabled for this product -> Redsys checkout (redirects away).
    if (product.cardPaymentEnabled) {
      const result = await startRedsysCheckout(product._id);
      if (result.needLogin) {
        navigate('/account');
      } else if (result.error) {
        setAccessMessage(result.error);
      }
      return;
    }

    // Otherwise the manual flow: create a pending purchase for admin approval.
    const authToken = localStorage.getItem('auth-token');
    if (!authToken) { navigate('/account'); return; }
    try {
      const response = await fetch(`${backendUrl}/cart/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'auth-token': authToken },
        body: JSON.stringify({ productId: product._id, price: product.price }),
      });
      const result = await response.json();
      if (result.success) {
        await refreshUserData();
        setHasAccess(false);
        setAccessMessage('Purchase successful! Your access is pending approval.');
      } else {
        setAccessMessage(result.message || 'Purchase failed. Please try again.');
      }
    } catch (error) {
      setAccessMessage('An error occurred. Please try again.');
    }
  };

  if (loading) return <div className="landing-page-loading">Loading...</div>;

  if (notFound) {
    return (
      <div className="landing-page-not-found">
        <h1>404</h1>
        <p>The page you're looking for doesn't exist.</p>
        <a href="/">Go to Homepage</a>
      </div>
    );
  }

  const product = landingPage.product;
  const event = product.event;
  const galleryImages = (landingPage.galleryImages || []).filter(Boolean);
  const heroImage = galleryImages.length === 0 ? product.featuredImage : null;
  const description = landingPage.description || product.description;
  const bgType = landingPage.backgroundType || 'color';
  const bgColor = landingPage.backgroundColor || '#141414';
  const bgImage = landingPage.backgroundImage || '';
  const bgOverlay = typeof landingPage.backgroundOverlay === 'number' ? landingPage.backgroundOverlay : 0.7;
  const buttonText = landingPage.buttonText || 'Play Now';
  const buttonEnabled = landingPage.buttonEnabled !== false;
  const buttonDisabledText = landingPage.buttonDisabledText || 'Coming Soon';
  const buttonColor = landingPage.buttonColor || '#ffffff';
  const buttonTextColor = landingPage.buttonTextColor || '#111111';
  const titleColor = landingPage.titleColor || '#ffffff';
  const descColor = landingPage.descriptionColor || 'rgba(255, 255, 255, 0.65)';
  const showMetaTags = landingPage.showMetaTags !== false;

  const projections = event?.video_types || [];
  const camNames = event?.cam_names || [];
  const audioNames = event?.audio_names || [];
  const hasWebRTC = !!event?.webrtc_manifest;

  const normalizeProjection = (p) => {
    const upper = (p || '').toUpperCase();
    if (upper === 'NONE') return '2D';
    if (upper === '360' || upper === '360_TB') return '360';
    if (upper === '180' || upper === '180_NONO') return '180';
    return '2D';
  };

  const uniqueProjections = [...new Set(projections.map(normalizeProjection))].filter(Boolean);
  const hasMeta = showMetaTags && (uniqueProjections.length > 0 || camNames.length > 0 || audioNames.length > 0 || hasWebRTC);

  const pageStyle = bgType === 'image' && bgImage
    ? { backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }
    : { backgroundColor: bgColor };

  return (
    <div className="landing-page" style={pageStyle}>
      {bgType === 'image' && bgImage && (
        <div className="landing-page-overlay" style={{ backgroundColor: `rgba(0, 0, 0, ${bgOverlay})` }} />
      )}
      <div className="landing-page-container">

        {galleryImages.length > 0 ? (
          <div className="landing-page-gallery">
            {galleryImages.map((img, idx) => (
              <div key={idx} className="landing-page-gallery-item">
                <img src={img} alt={`${product.title} ${idx + 1}`} className="landing-page-gallery-image" />
              </div>
            ))}
          </div>
        ) : heroImage ? (
          <div className="landing-page-image-wrapper">
            <img src={heroImage} alt={product.title} className="landing-page-image" />
          </div>
        ) : null}

        <h1 className="landing-page-title" style={{ color: titleColor }}>{product.title}</h1>

        {hasMeta && (
          <div className="landing-page-meta">
            {uniqueProjections.map((proj) => (
              <span key={proj} className="landing-page-tag">{proj}</span>
            ))}
            {hasWebRTC && <span className="landing-page-tag">WebRTC</span>}
            {camNames.length > 0 && (
              <span className="landing-page-tag">{camNames.length} {camNames.length === 1 ? 'Camera' : 'Cameras'}</span>
            )}
            {audioNames.length > 0 && (
              <span className="landing-page-tag">{audioNames.length} Audio</span>
            )}
          </div>
        )}

        {description && (
          <p className="landing-page-description" style={{ color: descColor }}>{description}</p>
        )}

        <div className="landing-page-cta">
          {!buttonEnabled ? (
            <button className="landing-page-btn landing-page-btn-disabled" disabled>
              {buttonDisabledText}
            </button>
          ) : isCheckingAccess ? (
            <p className="landing-page-status">Checking access...</p>
          ) : hasAccess === true ? (
            <button className="landing-page-btn" style={{ background: buttonColor, color: buttonTextColor }} onClick={handleCTA}>
              {buttonText}
            </button>
          ) : hasAccess === false && accessMessage ? (
            <p className="landing-page-status">{accessMessage}</p>
          ) : hasAccess === false ? (
            <button className="landing-page-btn landing-page-btn-secondary" onClick={handleBuyNow}>
              Purchase Now
            </button>
          ) : (
            <button className="landing-page-btn" style={{ background: buttonColor, color: buttonTextColor }} onClick={handleCTA}>
              {buttonText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default LandingPage;
